import getSymbolFromCurrency from 'currency-symbol-map';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Pressable, StyleSheet, Text, View } from 'react-native';
import { twoDp } from '../../../../../utils/twoDP';
import { countryAndCurrency } from '../../../../../utils/countryNamesWithCodes';
import { useBrandAllowedCurrency } from '../../../../../hooks/useBrandAllowedCurrency';
import VariantCombinationPicker from '../../../../common/VariantCombinationPicker';

// Type guard function to check if a brand is valid
function isBrand(brand: any): brand is TGetAllBrandsData {
	return brand !== undefined && brand !== null;
}

const ProductPriceInfo = ({
	variant,
	optionValue,
	highestPrice,
	availableVariantCombination,
	brand,
	variatorValues,
	variantOneValue,
	variantTwoValue,
	onVariantOneChange,
	onVariantTwoChange,
}: {
	variant: PopulatedVariant | Variant;
	highestPrice?: number;
	optionValue: string;
	availableVariantCombination: any;
	brand?: TGetAllBrandsData;
	variatorValues?: any[];
	variantOneValue?: string;
	variantTwoValue?: string;
	onVariantOneChange?: (value: string) => void;
	onVariantTwoChange?: (value: string) => void;
}) => {
	const { t } = useTranslation();

	const compareAtPrice: number = (variant?.option?.values.find(
		value => value.name === optionValue
	)?.compareAtPrice || availableVariantCombination?.comparePrice) as number;

	const discountPrice: number = variant?.option?.values.find(
		value => value.name === optionValue
	)?.price as number;

	const brandForCurrency = isBrand(brand) ? brand : undefined;
	const { findDefaultCountry, findMyCurrency, findMyCountry } =
		brandForCurrency
			? useBrandAllowedCurrency({ brand: brandForCurrency })
			: {
					findDefaultCountry: undefined,
					findMyCurrency: undefined,
					findMyCountry: undefined,
			  };

	const showVariantCombinationPicker =
		variatorValues && variatorValues.length > 0;

	return (
		<View style={styles.container}>
			<View style={styles.contentContainer}>
				<View style={styles.price}>
					{compareAtPrice > 0 && (
						<Text style={styles.startingPrice}>
							{getSymbolFromCurrency(
								(findMyCurrency?.currency ||
									findDefaultCountry?.currency ||
									'EUR') as string
							)}
							{twoDp(
								(compareAtPrice ?? highestPrice) *
									(+findMyCountry?.exchangeRate || 1)
							)}
						</Text>
					)}

					<Text style={styles.discountPrice}>
						{getSymbolFromCurrency(
							(findMyCurrency?.currency ||
								findDefaultCountry?.currency ||
								'EUR') as string
						)}
						{twoDp(
							((availableVariantCombination?.price ||
								discountPrice) as number) *
								(+findMyCountry?.exchangeRate || 1)
						)}
					</Text>
				</View>

				{showVariantCombinationPicker && variant && (
					<View style={styles.variantPickerContainer}>
						<VariantCombinationPicker
							variatorValues={variatorValues}
							variantOneValue={variantOneValue || ''}
							variantTwoValue={variantTwoValue || ''}
							onVariantOneChange={
								onVariantOneChange || (() => {})
							}
							onVariantTwoChange={
								onVariantTwoChange || (() => {})
							}
							product={{
								productId: {
									option: variant?.option,
								},
							}}
						/>
					</View>
				)}
			</View>
			<Pressable onPress={() => null}>
				<Text style={styles.labelLink}></Text>
			</Pressable>
		</View>
	);
};

const styles = StyleSheet.create({
	price: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	startingPrice: {
		fontStyle: 'normal',
		fontWeight: '600',
		lineHeight: 14,
		color: '#7C889B',
		marginRight: 10,
		textDecorationLine: 'line-through',
	},
	discountPrice: {
		fontStyle: 'normal',
		fontWeight: '600',
		lineHeight: 14,
		color: '#F96152',
	},
	labelLink: {
		fontStyle: 'normal',
		fontWeight: '600',
		fontSize: 14,
		lineHeight: 14,
		textDecorationLine: 'underline',
		color: '#6645F4',
	},
	container: {
		flexDirection: 'column',
		marginTop: 15,
		marginBottom: 10,
	},
	contentContainer: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		width: '100%',
	},
	variantPickerContainer: {
		flexShrink: 1,
		maxWidth: '60%',
	},
});

export default ProductPriceInfo;
